import { type CSSProperties, useMemo, useRef, useState } from 'react';
import { createCalendar } from '@internationalized/date';
import { useRangeCalendarState } from '@react-stately/calendar';
import { type DateValue, type RangeCalendarProps as PrimitiveCalendarProps, useRangeCalendar } from 'react-aria';
import { VerticalStack } from '../../../alignment/vertical-stack';
import type { DatePickerPagination } from '../util';
import { CalendarGrid } from './grid/calendar-grid';
import { CalendarHeader } from './header/calendar-header';
import { MonthGrid } from './month-grid/month-grid';
import type { CalendarMode } from './util';
import { YearGrid } from './year-grid/year-grid';
import styles from './calendar.module.scss';

const YEAR_SELECT_RANGE_COUNT = 28;

interface RangeCalendarProps extends PrimitiveCalendarProps<DateValue> {
  locale: string;
  pagination?: DatePickerPagination;
  style?: CSSProperties;
}

export function RangeCalendar({ locale, pagination, style, ...props }: RangeCalendarProps) {
  const [mode, setMode] = useState<CalendarMode>('day');
  const ref = useRef<HTMLDivElement>(null);
  const state = useRangeCalendarState({
    ...props,
    locale,
    createCalendar,
  });
  const { calendarProps, prevButtonProps, nextButtonProps } = useRangeCalendar(props, state, ref);
  const mergedStyle = useMemo(() => ({ ...calendarProps.style, ...style }), [calendarProps.style, style]);
  const { focusedDate, setFocusedDate } = state;

  return (
    <div {...calendarProps} className={styles.calendar} ref={ref} style={mergedStyle}>
      <VerticalStack>
        <CalendarHeader
          buttonPropsNext={nextButtonProps}
          buttonPropsPrev={prevButtonProps}
          focusedDate={state.focusedDate}
          mode={mode}
          setFocusedDate={setFocusedDate}
          setMode={setMode}
          yearSelectOptionCount={YEAR_SELECT_RANGE_COUNT}
        />
        {mode === 'day' ? <CalendarGrid locale={locale} pagination={pagination} state={state} /> : null}
        {mode === 'month' ? (
          <MonthGrid
            focusedMonth={state.focusedDate.month}
            onSelectMonth={(month) => {
              setFocusedDate(focusedDate.set({ day: 1, month }));
              setMode('day');
            }}
          />
        ) : null}
        {mode === 'year' ? (
          <YearGrid
            focusedYear={state.focusedDate.year}
            onSelectYear={(year) => {
              setFocusedDate(focusedDate.set({ day: 1, year }));
              setMode('day');
            }}
            yearOptions={YEAR_SELECT_RANGE_COUNT}
          />
        ) : null}
      </VerticalStack>
    </div>
  );
}
